import React, { useState } from "react";
import axios from "axios";
import { Star, CheckCircle, XCircle, RefreshCw, X } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

function StarRating({ value, onChange, label, testIdPrefix }) {
  return (
    <div>
      <p className="text-sm font-medium text-brand-navy mb-2">{label}</p>
      <div className="flex gap-2" role="group" aria-label={label}>
        {[1, 2, 3, 4, 5].map((n) => (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            data-testid={`${testIdPrefix}-star-${n}`}
            aria-label={`${n} star${n > 1 ? "s" : ""}`}
            className={`w-9 h-9 rounded-lg border-2 flex items-center justify-center transition-all ${
              n <= value ? "bg-brand-orange border-brand-orange text-white" : "border-gray-200 text-gray-300 hover:border-brand-orange/40"
            }`}
          >
            <Star size={14} fill={n <= value ? "currentColor" : "none"} />
          </button>
        ))}
      </div>
    </div>
  );
}

export default function PostSessionFeedbackModal({ session, onClose, onDone }) {
  const [didPlay, setDidPlay] = useState(null);      // true | false
  const [skillFit, setSkillFit] = useState(0);
  const [reliability, setReliability] = useState(0);
  const [playAgain, setPlayAgain] = useState(null);  // true | false
  const [step, setStep] = useState(1);               // 1=did-play, 2=ratings, 3=done
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const partner = session.partner_name || "Your partner";
  const sport = session.sport || "session";

  const submitFeedback = async (finalPlayAgain) => {
    setSaving(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      await axios.post(
        `${API}/api/sessions/${session.id}/review`,
        {
          skill_rating: skillFit || 3,
          reliability_rating: reliability || 3,
          did_play: didPlay,
          play_again: finalPlayAgain ?? false,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStep(3);
      setTimeout(() => onDone?.(), 1800);
    } catch (err) {
      setError(err.response?.data?.detail || "Could not save feedback");
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="feedback-title"
      className="fixed inset-0 bg-brand-navy/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      data-testid="post-session-feedback-modal"
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 relative">
        <button
          onClick={onClose}
          aria-label="Close feedback"
          className="absolute top-4 right-4 text-gray-400 hover:text-brand-navy transition-colors"
        >
          <X size={18} />
        </button>

        {/* ─── Step 1: Did you play? ─── */}
        {step === 1 && (
          <>
            <div className="text-center mb-5">
              <div className="text-4xl mb-3">🎾</div>
              <h2 id="feedback-title" className="text-xl font-heading font-bold text-brand-navy">
                Did you play?
              </h2>
              <p className="text-gray-500 text-sm mt-1">
                Your {sport} session with <strong>{partner}</strong> was scheduled.
                How did it go?
              </p>
            </div>
            {error && <p className="text-red-500 text-sm text-center mb-3">{error}</p>}
            <div className="grid grid-cols-2 gap-3">
              <button
                onClick={() => { setDidPlay(true); setStep(2); }}
                data-testid="feedback-did-play-yes"
                className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-brand-green hover:bg-brand-green/5 transition-all"
              >
                <CheckCircle size={28} className="text-brand-green" />
                <span className="font-semibold text-brand-navy text-sm">Yes, we played!</span>
              </button>
              <button
                onClick={() => { setDidPlay(false); submitFeedback(false); }}
                data-testid="feedback-did-play-no"
                className="flex flex-col items-center gap-2 p-4 border-2 border-gray-200 rounded-xl hover:border-red-400 hover:bg-red-50 transition-all"
                disabled={saving}
              >
                <XCircle size={28} className="text-red-400" />
                <span className="font-semibold text-brand-navy text-sm">{saving ? "Saving…" : "No-show / Cancelled"}</span>
              </button>
            </div>
          </>
        )}

        {/* ─── Step 2: Ratings ─── */}
        {step === 2 && (
          <>
            <h2 id="feedback-title" className="text-xl font-heading font-bold text-brand-navy mb-1">How was the match?</h2>
            <p className="text-gray-500 text-sm mb-5">Rate your session with <strong>{partner}</strong></p>
            {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
            <div className="space-y-5">
              <StarRating value={skillFit} onChange={setSkillFit} label="Skill fit — were you well-matched?" testIdPrefix="skill-fit" />
              <StarRating value={reliability} onChange={setReliability} label="Reliability — did they show up on time?" testIdPrefix="reliability" />
              <div>
                <p className="text-sm font-medium text-brand-navy mb-2">Would you play again?</p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setPlayAgain(true)}
                    data-testid="play-again-yes"
                    className={`flex-1 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${playAgain === true ? "bg-brand-green/10 border-brand-green text-brand-green" : "border-gray-200 text-gray-600"}`}
                  >
                    <RefreshCw size={14} className="inline mr-1.5" />Yes
                  </button>
                  <button
                    onClick={() => setPlayAgain(false)}
                    data-testid="play-again-no"
                    className={`flex-1 py-2 rounded-xl border-2 text-sm font-semibold transition-all ${playAgain === false ? "bg-red-50 border-red-300 text-red-600" : "border-gray-200 text-gray-600"}`}
                  >
                    Not this time
                  </button>
                </div>
              </div>
            </div>
            <button
              onClick={() => submitFeedback(playAgain)}
              disabled={skillFit === 0 || reliability === 0 || saving}
              data-testid="submit-feedback-btn"
              className="w-full mt-5 py-3 bg-brand-orange text-white font-bold rounded-xl hover:bg-brand-orange-hover disabled:opacity-40 transition-colors"
            >
              {saving ? "Saving…" : "Submit Feedback"}
            </button>
          </>
        )}

        {/* ─── Step 3: Done ─── */}
        {step === 3 && (
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-brand-green/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle size={32} className="text-brand-green" />
            </div>
            <h2 className="text-xl font-heading font-bold text-brand-navy mb-1">Thanks for the feedback!</h2>
            <p className="text-gray-500 text-sm">Your reliability score has been updated.</p>
          </div>
        )}
      </div>
    </div>
  );
}
